import { useCallback, useRef } from "react";
import type { GridReadyEvent, RowClickedEvent } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import type { CostCenter } from "../models/CostCenter";
import { useTableData } from "../hooks/useTableData";

interface CostCentersTableProps {
	costCenters: CostCenter[];
	selectedCostCenter: CostCenter | null;
	onRowSelected: (costCenter: CostCenter | null) => void;
	onDelete: (costCenter: CostCenter) => void;
}

export const CostCentersTable = ({
	costCenters,
	selectedCostCenter,
	onRowSelected,
	onDelete,
}: CostCentersTableProps) => {
	const gridRef = useRef<AgGridReact<CostCenter>>(null);
	const { columnDefs } = useTableData({ onDelete });

	const onGridReady = useCallback((_params: GridReadyEvent<CostCenter>) => {
		// Let flex columns handle sizing naturally
	}, []);

	const onRowClicked = useCallback(
		(event: RowClickedEvent<CostCenter>) => {
			if (event.data) {
				onRowSelected(event.data);
			}
		},
		[onRowSelected],
	);

	const getRowId = useCallback((params: { data: CostCenter }) => {
		return params.data.Id?.toString() ?? "";
	}, []);

	return (
		<AgGridReact<CostCenter>
			ref={gridRef}
			rowData={costCenters}
			columnDefs={columnDefs}
			defaultColDef={globalDefaultColDef}
			onGridReady={onGridReady}
			onRowClicked={onRowClicked}
			getRowId={getRowId}
			rowSelection="single"
			suppressRowClickSelection={false}
			suppressContextMenu
			alwaysShowVerticalScroll
			alwaysShowHorizontalScroll
			suppressColumnVirtualisation
			animateRows={false}
			rowHeight={36}
			headerHeight={36}
			getRowStyle={(params) => {
				if (selectedCostCenter?.Id === params.data?.Id) {
					return { backgroundColor: "#e3f2fd", color: "inherit" };
				}
				if (params.data && !params.data.Active) {
					return { backgroundColor: "#f5f5f5", color: "#9e9e9e" };
				}
				return undefined;
			}}
		/>
	);
};
